/**
 * @file menu-actions.ts
 * @description Пункты «⋯» строки меню (phase19.16 Р1; uiux-design-r1 §3:
 * Изменить · Выше · Ниже · Вложить в пункт выше / Вынести на верхний
 * уровень · Скрыть с сайта / Показать · Удалить). «Выше» и «Ниже» —
 * запасной путь без перетаскивания для касания и клавиатуры.
 */

import { ArrowDown, ArrowUp, Eye, EyeOff, IndentDecrease, IndentIncrease, Pencil, Trash2 } from "lucide-react";

import type { RowAction } from "../../ui/RowActionsMenu";
import { indent, indentBlocker, locate, moveBy, outdent } from "../../../lib/site-view/menu-tree";
import type { SiteMenuItem } from "../../../lib/site-view/types";

export interface MenuActionHandlers {
  edit: (item: SiteMenuItem, nested: boolean) => void;
  reorder: (tree: SiteMenuItem[]) => void;
  toggleHidden: (item: SiteMenuItem) => void;
  remove: (item: SiteMenuItem) => void;
}

export function menuRowActions(tree: SiteMenuItem[], item: SiteMenuItem, nested: boolean, h: MenuActionHandlers): RowAction[] {
  const at = locate(tree, item.id);
  const siblings = at && at.parent >= 0 ? tree[at.parent]!.children.length : tree.length;
  const apply = (next: SiteMenuItem[] | null) => () => {
    if (next) h.reorder(next);
  };
  const actions: RowAction[] = [
    { key: "edit", label: "Изменить", icon: Pencil, run: () => h.edit(item, nested) },
    { key: "up", label: "Выше", icon: ArrowUp, disabled: !at || at.index === 0, run: apply(moveBy(tree, item.id, -1)) },
    { key: "down", label: "Ниже", icon: ArrowDown, disabled: !at || at.index >= siblings - 1, run: apply(moveBy(tree, item.id, 1)) },
  ];
  if (nested) {
    actions.push({ key: "outdent", label: "Вынести на верхний уровень", icon: IndentDecrease, run: apply(outdent(tree, item.id)) });
  } else {
    const blocker = indentBlocker(tree, item.id);
    actions.push({
      key: "indent",
      label: blocker && item.children.length > 0 ? "Вложить нельзя: есть вложенные" : "Вложить в пункт выше",
      icon: IndentIncrease,
      disabled: blocker !== null,
      run: apply(indent(tree, item.id)),
    });
  }
  actions.push(
    item.isHidden
      ? { key: "show", label: "Показать на сайте", icon: Eye, run: () => h.toggleHidden(item) }
      : { key: "hide", label: "Скрыть с сайта", icon: EyeOff, run: () => h.toggleHidden(item) },
    { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => h.remove(item) },
  );
  return actions;
}
